/* RESPONSIBLE TEAM: team-inbox-experience */
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import storage from 'embercom/vendor/intercom/storage';
import type Session from 'embercom/services/session';

export default class ConversationStreamSettings extends Service {
  @service declare session: Session;
  @tracked hideEvents = this.storedValueOrDefault;
  @tracked showCustomerTimezone = this.storedShowCustomerTimezoneOrDefault;
  @tracked currentCustomerTimezone: string | undefined = undefined;

  get storedValueOrDefault() {
    if (storage.get(this.localStorageHideEventsKey) !== null) {
      return storage.get(this.localStorageHideEventsKey);
    }
    return true;
  }

  get storedShowCustomerTimezoneOrDefault() {
    if (storage.get(this.localStorageShowCustomerTimezoneKey) !== null) {
      return storage.get(this.localStorageShowCustomerTimezoneKey);
    }
    return false;
  }

  get localStorageHideEventsKey() {
    let appId = this.session.isWorkspaceLoaded ? this.session.workspace.id : 'unknown';
    let adminId = this.session.isTeammateLoaded ? this.session.teammate.id : 'unknown';
    return `${appId}::${adminId}::ConversationStreamSettings::hideEvents`;
  }

  get localStorageShowCustomerTimezoneKey() {
    let appId = this.session.isWorkspaceLoaded ? this.session.workspace.id : 'unknown';
    let adminId = this.session.isTeammateLoaded ? this.session.teammate.id : 'unknown';
    return `${appId}::${adminId}::ConversationStreamSettings::showCustomerTimezone`;
  }

  get effectiveCustomerTimezone(): string | undefined {
    if (!this.session.workspace.isFeatureEnabled('spg-inbox-management-customer-timezone')) {
      return undefined;
    }
    if (!this.showCustomerTimezone) {
      return undefined;
    }
    return this.currentCustomerTimezone || undefined;
  }

  @action toggleEvents(toggle?: boolean): void {
    if (toggle === undefined) {
      this.hideEvents = !this.hideEvents;
    } else {
      this.hideEvents = toggle;
    }
    storage.set(this.localStorageHideEventsKey, this.hideEvents);
    window.dispatchEvent(new CustomEvent('conversation-stream-settings:change'));
  }

  @action toggleCustomerTimezone(toggle?: boolean): void {
    if (toggle === undefined) {
      this.showCustomerTimezone = !this.showCustomerTimezone;
    } else {
      this.showCustomerTimezone = toggle;
    }
    storage.set(this.localStorageShowCustomerTimezoneKey, this.showCustomerTimezone);
  }
}

declare module '@ember/service' {
  interface Registry {
    conversationStreamSettings: ConversationStreamSettings;
    'conversation-stream-settings': ConversationStreamSettings;
  }
}
